/**
 * VirtualServerBundleCard - Hero component for one-click tool bundle selection
 * 
 * Virtual Servers are curated tool bundles designed for specific agent use cases.
 * This component enables users to select an entire bundle with a single click,
 * dramatically simplifying the agent creation workflow.
 * 
 * Blue Steel Score Target: 90+
 * - Intelligent Design: Shows tool count, purpose, preview
 * - Dynamic Adaptation: Hover reveals tool list
 * - WOW Factor: Smooth animations, glow effects, satisfying selection
 * 
 * @example
 * ```tsx
 * <VirtualServerBundleCard
 *   virtualServer={vs}
 *   selected={selectedVsId === vs.id}
 *   onSelect={() => selectAllToolsFromVS(vs)}
 *   toolPreview={['create_ticket', 'search_tickets', 'update_ticket']}
 * />
 * ```
 */

import React, { useState, useCallback } from 'react';
import type { VirtualServer } from './types';

// Re-export for convenience
export type { VirtualServer };

export interface VirtualServerBundleCardProps {
  virtualServer: VirtualServer;
  selected?: boolean;
  onSelect?: (virtualServer: VirtualServer) => void;
  toolPreview?: string[];  // First 3-5 tool names for preview
  disabled?: boolean;
  size?: 'compact' | 'normal' | 'large';
}

// Default icons by common VS name patterns
const getDefaultIcon = (name: string): string => {
  const nameLower = name.toLowerCase();
  if (nameLower.includes('jira')) return '🎫';
  if (nameLower.includes('sap')) return '📊';
  if (nameLower.includes('salesforce')) return '☁️';
  if (nameLower.includes('servicenow')) return '🔧';
  if (nameLower.includes('identity') || nameLower.includes('iam')) return '🔐';
  if (nameLower.includes('devops') || nameLower.includes('github')) return '🚀';
  if (nameLower.includes('slack') || nameLower.includes('teams')) return '💬';
  if (nameLower.includes('email') || nameLower.includes('outlook')) return '📧';
  if (nameLower.includes('calendar')) return '📅';
  if (nameLower.includes('finance') || nameLower.includes('payment')) return '💰';
  if (nameLower.includes('hr') || nameLower.includes('workday')) return '👥';
  if (nameLower.includes('analytics')) return '📈';
  return '📦';
};

export const VirtualServerBundleCard: React.FC<VirtualServerBundleCardProps> = ({
  virtualServer,
  selected = false,
  onSelect,
  toolPreview = [],
  disabled = false,
  size = 'normal',
}) => {
  const [hovered, setHovered] = useState(false);

  // Blue Steel: Disable bundles with no tools (Zero Friction - no dead ends!)
  // NOTE: tool_count can be stale/derived; we still want consistent UX + correct a11y styling.
  const hasNoTools = virtualServer.tool_count === 0 && toolPreview.length === 0;
  const isDisabled = disabled || hasNoTools;

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled && onSelect) {
      console.log('[VirtualServerBundleCard] Bundle clicked', {
        bundleId: virtualServer.id,
        bundleName: virtualServer.name,
        toolCount: virtualServer.tool_count,
        disabled: isDisabled,
        hasOnSelect: !!onSelect,
      });
      onSelect(virtualServer);
    } else {
      console.warn('[VirtualServerBundleCard] Bundle click ignored', {
        bundleId: virtualServer.id,
        disabled: isDisabled,
        hasOnSelect: !!onSelect,
      });
    }
  }, [isDisabled, onSelect, virtualServer]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (!isDisabled && onSelect) {
        onSelect(virtualServer);
      }
    }
  }, [isDisabled, onSelect, virtualServer]);

  const icon = virtualServer.icon || getDefaultIcon(virtualServer.name);
  
  // Size-based styles
  const sizeStyles = {
    compact: { padding: 12, iconSize: 28, fontSize: 12 },
    normal: { padding: 16, iconSize: 36, fontSize: 13 },
    large: { padding: 20, iconSize: 44, fontSize: 14 },
  }[size];

  return (
    <div
      role="option"
      aria-selected={selected}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: sizeStyles.padding,
        background: selected
          ? 'linear-gradient(135deg, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 20%, transparent), color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 10%, transparent))'
          : hovered
          ? 'var(--card-hover-bg)'
          : 'var(--card-bg)',
        border: `2px solid ${selected ? 'var(--quantum-violet, var(--main-accent))' : 'var(--border-color)'}`,
        borderRadius: 14,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hovered && !isDisabled ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        boxShadow: selected
          ? '0 0 20px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 30%, transparent), 0 8px 24px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 15%, transparent)'
          : hovered
          ? 'var(--shadow-lg)'
          : 'var(--shadow-md)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Selection glow effect */}
      {selected && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 30%, color-mix(in srgb, var(--main-accent) 10%, transparent), transparent 70%)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Icon */}
        <div style={{
          width: sizeStyles.iconSize,
          height: sizeStyles.iconSize,
          borderRadius: 12,
          background: selected
            ? 'var(--quantum-violet, var(--main-accent))'
            : 'color-mix(in srgb, var(--main-accent) 15%, transparent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: sizeStyles.iconSize * 0.5,
          flexShrink: 0,
          transition: 'all 0.2s ease',
          boxShadow: selected ? '0 4px 12px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 40%, transparent)' : 'none',
        }}>
          {icon}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name + Tool Count */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 4,
          }}>
            <span style={{
              fontSize: sizeStyles.fontSize,
              fontWeight: 600,
              color: 'var(--main-text)',
            }}>
              {virtualServer.display_name || virtualServer.name}
            </span>
            <span style={{
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
              background: selected
                ? 'var(--quantum-violet, var(--main-accent))'
                : 'color-mix(in srgb, var(--main-accent) 20%, transparent)',
              color: selected ? 'white' : 'var(--main-accent)',
              transition: 'all 0.2s ease',
            }}>
              {virtualServer.tool_count} tools
            </span>
          </div>

          {/* Description */}
          <div style={{
            fontSize: 11,
            color: 'var(--main-text-secondary)',
            lineHeight: 1.4,
            marginBottom: size !== 'compact' ? 8 : 0,
          }}>
            {virtualServer.description || `Tools for ${virtualServer.name}`}
          </div>

          {/* Recommended For (if available) */}
          {virtualServer.recommended_for && size !== 'compact' && (
            <div style={{
              fontSize: 10,
              color: 'var(--info-color)',
              marginBottom: 8,
            }}>
              💡 Recommended for: {virtualServer.recommended_for}
            </div>
          )}

          {/* Tool Preview (on hover or always in large mode) */}
          {(hovered || size === 'large') && toolPreview.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              marginTop: 8,
              animation: 'fadeSlideUp 0.2s ease',
            }}>
              {toolPreview.slice(0, 4).map((toolName, idx) => (
                <span
                  key={toolName}
                  style={{
                    padding: '2px 6px',
                    borderRadius: 6,
                    fontSize: 9,
                    background: 'color-mix(in srgb, var(--main-text) 8%, transparent)',
                    color: 'var(--main-text-secondary)',
                    fontFamily: 'var(--font-mono)',
                    animationDelay: `${idx * 30}ms`,
                  }}
                >
                  {toolName}
                </span>
              ))}
              {toolPreview.length > 4 && (
                <span style={{
                  padding: '2px 6px',
                  borderRadius: 6,
                  fontSize: 9,
                  background: 'color-mix(in srgb, var(--main-accent) 15%, transparent)',
                  color: 'var(--main-accent)',
                }}>
                  +{toolPreview.length - 4} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Selection Indicator */}
        <div
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            border: `2px solid ${selected ? 'var(--quantum-violet, var(--main-accent))' : 'var(--border-color)'}`,
            background: selected ? 'var(--quantum-violet, var(--main-accent))' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: selected ? 'scale(1.1)' : 'scale(1)',
            boxShadow: selected ? '0 2px 8px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 40%, transparent)' : 'none',
          }}
        >
          {selected && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7L5.5 10.5L12 3.5"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Blue Steel: Always-visible call-to-action */}
      <div style={{
        marginTop: size === 'compact' ? 8 : 12,
        paddingTop: size === 'compact' ? 8 : 10,
        borderTop: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        position: 'relative',
        zIndex: 1,
      }}>
        {selected ? (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--success-color)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7L5.5 10.5L12 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {virtualServer.tool_count} tools selected — click to deselect
          </span>
        ) : (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: hovered ? 'var(--main-accent)' : 'var(--main-text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'color 0.2s ease',
          }}>
            <span style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              background: hovered 
                ? 'var(--quantum-violet, var(--main-accent))' 
                : 'color-mix(in srgb, var(--main-accent) 15%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}>
              <span style={{ 
                color: hovered ? 'white' : 'var(--main-accent)',
                fontSize: 14,
                fontWeight: 700,
              }}>+</span>
            </span>
            Click to add all {virtualServer.tool_count} tools
          </span>
        )}
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

